import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.menu_api.price_changes;

// Helper function to configure the Axios instance
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'menu_api',
  group: 'price_changes',
  defaultTtl: '2m',
  hashLargeSegments: true,
  emitMeta: true,
});

// Types
export interface PriceChange {
  product_id: string;
  product_name: string;
  timestamp: string;
  local_id: number;
  old_price: number;
  new_price: number;
  user_id: string;
  user_name: string;
  price_type: 'restaurant' | 'delivery' | 'online' | 'caja' | 'all';
}

export interface LocalPriceChangesParams {
  days?: number;
  start_date?: string;
  end_date?: string;
}

export interface ProductPriceHistoryParams {
  item_id?: number;
  group_id?: number;
  option_id?: number;
  days?: number;
  start_date?: string;
  end_date?: string;
}

/**
 * Fetch all price changes for a specific local.
 * 
 * By default returns the last 30 days of changes.
 * You can specify a custom number of days using the 'days' parameter,
 * or provide a specific date range using 'start_date' and 'end_date'.
 * 
 * If start_date and end_date are provided, they take precedence over 'days'.
 * 
 * @param local_id - The unique identifier of the local
 * @param params - Optional query parameters (days, start_date, end_date)
 */
export const getPriceChangesByLocalId = async (
  local_id: number,
  params?: LocalPriceChangesParams
) => {
  const url = `/GetPriceChangesByLocalId/${local_id}`;
  const body = {
    ...(params?.days !== undefined && { days: params.days }),
    ...(params?.start_date && { start_date: params.start_date }),
    ...(params?.end_date && { end_date: params.end_date }),
  };
  
  return await retryWithBackoff(() => api.post(url, body));
};

/**
 * Fetch price change history for a specific product.
 * 
 * Product identification rules:
 * - Only item_id provided: looks for IT-{item_id} (regular item)
 * - item_id + group_id provided: looks for IG-{item_id}-{group_id} (selection group item)
 * - Only option_id provided: looks for OP-{option_id} (modifier option)
 * 
 * Invalid combinations that will return an error:
 * - All three (item_id, group_id, option_id) provided
 * - option_id + group_id (without item_id)
 * - item_id + option_id (without group_id)
 * 
 * By default returns the last 30 days of changes.
 * You can specify a custom number of days using the 'days' parameter,
 * or provide a specific date range using 'start_date' and 'end_date'.
 * 
 * @param params - Query parameters for product identification and date filtering
 */
export const getPriceChangeHistoryForProduct = async (
  params: ProductPriceHistoryParams
) => {
  const url = `/GetPriceChangeHistoryForProduct`;
  const body = {
    ...(params.item_id !== undefined && { item_id: params.item_id }),
    ...(params.group_id !== undefined && { group_id: params.group_id }),
    ...(params.option_id !== undefined && { option_id: params.option_id }),
    ...(params.days !== undefined && { days: params.days }),
    ...(params.start_date && { start_date: params.start_date }),
    ...(params.end_date && { end_date: params.end_date }),
  };
  
  return await retryWithBackoff(() => api.post(url, body));
};

// Convenience functions for specific product types

/**
 * Fetch price change history for a regular item (IT-{item_id})
 */
export const getPriceChangeHistoryForItem = async (
  item_id: number,
  params?: Omit<ProductPriceHistoryParams, 'item_id' | 'group_id' | 'option_id'>
) => {
  return getPriceChangeHistoryForProduct({
    item_id,
    ...params
  });
};

/**
 * Fetch price change history for a selection group item (IG-{item_id}-{group_id})
 */
export const getPriceChangeHistoryForSelectionGroupItem = async (
  item_id: number,
  group_id: number,
  params?: Omit<ProductPriceHistoryParams, 'item_id' | 'group_id' | 'option_id'>
) => {
  return getPriceChangeHistoryForProduct({
    item_id,
    group_id,
    ...params
  });
};

/**
 * Fetch price change history for a modifier option (OP-{option_id})
 */
export const getPriceChangeHistoryForModifierOption = async (
  option_id: number,
  params?: Omit<ProductPriceHistoryParams, 'item_id' | 'group_id' | 'option_id'>
) => {
  return getPriceChangeHistoryForProduct({
    option_id,
    ...params
  });
};
